import Activity from "../entities/activity";
import ActivityWindow from "../entities/activityWindow";
import * as util from "../helpers/util";
import { Glue42Core } from "@glue42/core";
import { promisify } from "../helpers/util";

// A helper class that provides lame activity AGM implementation
// Can be initialized with or without activity - in case no activity (independent windows outside activity) only
// subset of the methods work
export class ActivityAGM {
    public static AGM: Glue42Core.AGM.API;

    private _activity: Activity;

    constructor(activity: Activity) {
        this._activity = activity;
    }

    public register(definition: string | Glue42Core.AGM.MethodDefinition, handler: (args: object, caller: Glue42Core.AGM.Instance) => object) {
        this._ensureHasAgm();
        ActivityAGM.AGM.register(definition, handler);
    }

    public servers(): Glue42Core.AGM.Instance[] {
        this._ensureHasAgm();

        if (util.isUndefinedOrNull(this._activity)) {
            // or return ActivityAGM.AGM.servers()
            return [];
        }

        return this._activity.windows.map(
            (w: ActivityWindow) => {
                return w.instance;
            });
    }

    public methods(): Glue42Core.AGM.MethodDefinition[] {
        this._ensureHasAgm();

        if (util.isUndefinedOrNull(this._activity)) {
            // or return ActivityAGM.AGM.methods()
            return [];
        }

        const windows = this._activity.windows;
        const methodNames: string[] = [];
        const methods: Glue42Core.AGM.MethodDefinition[] = [];

        // get all windows and their methods, then assemble distinct methods
        windows.forEach((window) => {
            const windowMethods = this.methodsForWindow(window);
            windowMethods.forEach((currentWindowMethod) => {
                if (methodNames.indexOf(currentWindowMethod.name) === -1) {
                    methodNames.push(currentWindowMethod.name);
                    methods.push(currentWindowMethod);
                }
            });
        });

        return methods;
    }

    public methodsForWindow(window: ActivityWindow): Glue42Core.AGM.MethodDefinition[] {
        this._ensureHasAgm();
        // get instance and use agm to get methods for instance
        if (!window.instance) {
            return [];
        }

        return ActivityAGM.AGM.methodsForInstance(window.instance);
    }

    /** Possible invoke targets
     *
     * "activity.all" - [default]
     * "activity.best"
     * ActivityWindow
     * [ActivityWindow] -
     *
     * classic AGM
     * "all" - agm all
     * "best" - agm best
     * instance - agm
     * [ instances ]
     *
     */
    public invoke(
        methodName: string,
        arg: object,
        target: string | Glue42Core.AGM.Instance | ActivityWindow | Glue42Core.AGM.Instance[] | ActivityWindow[],
        options?: Glue42Core.AGM.InvokeOptions,
        success?: () => void,
        error?: () => void): Promise<object> {

        this._ensureHasAgm();
        const activityServers = this.servers();
        let serversToInvokeAgainst: Glue42Core.AGM.Instance[] = [];

        if (util.isUndefinedOrNull(target)) {
            target = "activity.all";
        }

        if (util.isString(target)) {
            if (target === "activity.all") {
                serversToInvokeAgainst = activityServers;
            } else if (target === "activity.best") {
                const potentialTargets = activityServers.filter((server) => {
                    const methods = ActivityAGM.AGM.methodsForInstance(server);
                    return methods.filter((m) => {
                        return m.name === methodName;
                    }).length > 0;
                });
                if (potentialTargets.length > 0) {
                    serversToInvokeAgainst = [potentialTargets[0]];
                }
            } else if (target === "all" || target === "best") {
                return promisify(ActivityAGM.AGM.invoke(methodName, arg, target, options), success, error);
            } else {
                // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                throw new Error("Invalid invoke target " + target);
            }
        } else if (util.isArray(target)) {
            // if the array is not empty
            if ((target as any).length >= 0) {
                const firstElem = (target as any)[0];
                // check argument type
                if (this._isInstance(firstElem)) {
                    serversToInvokeAgainst = (target as Glue42Core.AGM.Instance[]).map((instance: Glue42Core.AGM.Instance) => instance);
                } else if (this._isActivityWindow(firstElem)) {
                    serversToInvokeAgainst = (target as ActivityWindow[]).map((win: ActivityWindow) => win.instance);
                } else {
                    throw new Error("Unknown target object");
                }
            }
        } else {
            if (this._isInstance(target)) {
                serversToInvokeAgainst = [target as Glue42Core.AGM.Instance];
            } else if (this._isActivityWindow(target)) {
                serversToInvokeAgainst = [(target as ActivityWindow).instance];
            } else {
                throw new Error("Unknown target object");
            }
        }

        throw new Error("Not implemented");
        // return promisify(ActivityAGM.AGM.invoke(methodName, arg, serversToInvokeAgainst, options), success, error);
    }

    public unregister(definition: string | Glue42Core.AGM.MethodDefinition) {
        this._ensureHasAgm();
        return ActivityAGM.AGM.unregister(definition);
    }

    public createStream(methodDefinition: string | Glue42Core.AGM.MethodDefinition,
        subscriptionAddedHandler: (request: Glue42Core.AGM.StreamSubscription) => void,
        subscriptionRemovedHandler: (request: Glue42Core.AGM.StreamSubscription) => void) {
        this._ensureHasAgm();

        ActivityAGM.AGM.createStream(methodDefinition, {
            subscriptionAddedHandler,
            subscriptionRemovedHandler,
            subscriptionRequestHandler: undefined
        });
    }

    public subscribe(methodDefinition: string | Glue42Core.AGM.MethodDefinition, parameters: object, target: string | Glue42Core.AGM.Instance | Glue42Core.AGM.Instance[]): Promise<Glue42Core.AGM.Subscription> {
        this._ensureHasAgm();
        // #deleteme TODO: check servers needed?
        // const servers = this.servers();

        return ActivityAGM.AGM.subscribe(methodDefinition, parameters);
    }

    public _ensureHasAgm() {
        if (util.isUndefinedOrNull(ActivityAGM.AGM)) {
            throw new Error("Agm should be configured to be used in activity");
        }
    }

    public _isInstance(obj: any) {
        // lame check
        return obj.application !== undefined;
    }

    public _isActivityWindow(obj: any) {
        return obj.instance !== undefined;
    }
}
